import { AsyncOperationEvent } from "@inrange/building-manager-api-client";

// How many steps stay on screen while a run is in flight. A long investigation
// emits tens of these; showing them all pushes the answer off screen and makes
// the DOM grow for something nobody scrolls back through mid-run. The complete
// record is kept - it lands in the collapsed disclosure once the run ends.
const LIVE_STEP_LIMIT = 12;

const KIND_STYLE: Record<string, { colour: string; prefix: string }> = {
  // Reasoning reads as narration, so it is de-emphasised and unprefixed; actions
  // are the thing an operator is actually watching for, so they get the arrow.
  thinking: { colour: "#6c757d", prefix: "" },
  tool: { colour: "#495057", prefix: "→ " },
  tool_result: { colour: "#868e96", prefix: "   " },
};

/**
 * The live feed of what the agent is doing, while it is doing it.
 *
 * Replaces a single rolling label that could only ever say "Investigating
 * (5 steps)" - true, and useless for judging whether a run is making progress or
 * stuck. Rendered expanded during a run because that is the only time anyone
 * watches it; once the run finishes it collapses into {@link AgentStepRecord},
 * which reads the durable copy rather than this transient one.
 */
export const AgentStepFeed = ({
  events,
}: {
  events: AsyncOperationEvent[] | undefined;
}) => {
  if (!events?.length) return null;
  return (
    <ul
      className="list-unstyled mt-2 mb-0"
      style={{ fontSize: "0.8em", lineHeight: 1.5 }}
    >
      {events.slice(-LIVE_STEP_LIMIT).map((ev, i) => {
        const style = KIND_STYLE[ev.kind] ?? KIND_STYLE.tool;
        return (
          <li
            key={`${ev.at}-${i}`}
            style={{
              color: style.colour,
              fontStyle: ev.kind === "thinking" ? "italic" : undefined,
              // A tool call and its result are often near-identical strings; the
              // ellipsis keeps each on one line so the list stays scannable.
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={ev.text}
          >
            {style.prefix}
            {ev.text}
          </li>
        );
      })}
    </ul>
  );
};

/**
 * The same account, after the run: collapsed, and read from the durable record.
 *
 * The live feed lives on the operation row, which is TTL'd - so the moment a run
 * finishes, the account of how the answer was reached starts expiring while the
 * answer persists. This reads the copy the worker wrote next to the AI trace.
 * Distinct from the trace disclosure beside it, which is the same interaction at
 * a completely different resolution: every prompt and tool result, for debugging
 * rather than for "what did it actually do?".
 */
export const AgentStepRecord = ({
  steps,
  isLoading,
  onOpen,
}: {
  steps: string | null | undefined;
  isLoading: boolean;
  onOpen: (open: boolean) => void;
}) => (
  <details
    className="mt-2"
    onToggle={(e) => onOpen((e.target as HTMLDetailsElement).open)}
  >
    <summary
      style={{ cursor: "pointer", fontSize: "0.85em" }}
      className="text-muted"
    >
      Show steps taken
    </summary>
    <div className="mt-2">
      {isLoading ? (
        <div className="text-muted small">Loading steps...</div>
      ) : steps ? (
        <pre
          className="mb-0 p-2 rounded"
          style={{
            background: "rgba(0,0,0,0.04)",
            fontSize: "0.78em",
            lineHeight: 1.5,
            maxHeight: "22em",
            overflow: "auto",
            whiteSpace: "pre-wrap",
          }}
        >
          {steps}
        </pre>
      ) : (
        <div className="text-muted small">
          No step record for this run — it predates step recording, or the run
          was cut off before its final write.
        </div>
      )}
    </div>
  </details>
);
